import { Button, Spin, Layout, Empty, theme, Tooltip, Input, Modal, Form, Menu, Upload } from 'antd';
import type { MenuProps } from 'antd';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
    PlusOutlined,
    StarFilled,
    SearchOutlined,
    InboxOutlined,
    RollbackOutlined,
    LinkOutlined,
    FileTextOutlined,
    UploadOutlined,
    DeleteOutlined,
} from '@ant-design/icons';

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { ChangeEvent, MouseEvent as ReactMouseEvent } from 'react';
import type { Todo } from '../../types';
import {
    useCreateTodoMutation,
    useGetTodosQuery,
    useGetArchivedTodosQuery,
    useUpdateTodoMutation,
    useArchiveTodoMutation,
    useRestoreTodoMutation,
    useResolveTrackerIssueMutation,
    useUploadTodoReportMutation,
    useDeleteTodoReportMutation,
} from '../store/todos';
import { usePrompt } from '../hooks/usePrompt';
import { useConfirm } from '../hooks/useConfirm';
import useEventListener from '../hooks/useEventListener';
import { getNotificationInstance } from '../utils/notification';
import { resolvePublicUrl } from '../utils/publicUrl';
import { HTML_REPORT_ACCEPT, validateHtmlReportFile } from '../utils/reports';
import { setArchiveViewController } from '../utils/navigation';

const { Sider } = Layout;

const TRACKER_KEY_RE = /\b([A-Z][A-Z0-9]*-\d+)\b/i;

interface EpicContextMenuState {
    todo: Todo;
    x: number;
    y: number;
}

interface TruncatedEpicNameProps {
    text: string;
}

const TruncatedEpicName: React.FC<TruncatedEpicNameProps> = ({ text }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [truncated, setTruncated] = useState(false);

    const checkTruncation = () => {
        const el = ref.current;
        if (!el) {
            return;
        }
        setTruncated(el.scrollWidth > el.clientWidth + 1);
    };

    useEffect(() => {
        checkTruncation();
    }, [text]);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(() => {
            checkTruncation();
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Tooltip title={truncated ? text : undefined} placement="right">
            <span ref={ref} className="app-sider-item__title">{text}</span>
        </Tooltip>
    );
};

interface TodosSiderProps {
    projectUrl: string;
    activeTodoId?: number;
}

const TodosSider: React.FC<TodosSiderProps> = ({ activeTodoId, projectUrl }: TodosSiderProps) => {
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const { prompt, promptModal } = usePrompt();
    const { confirm, confirmModal } = useConfirm();
    const [collapsed, setCollapsed] = useState(false);
    const [showArchive, setShowArchive] = useState(false);
    const [selectedTodos, setSelectedTodos] = useState<number[]>([]);
    const [search, setSearch] = useState('');
    const searchInputRef = useRef<any>(null);

    useEffect(() => {
        setArchiveViewController(setShowArchive);
        return () => setArchiveViewController(null);
    }, []);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [createForm] = Form.useForm<{ todoName: string; trackerLink?: string }>();
    const createTrackerLink = Form.useWatch('trackerLink', createForm);
    const autoFilledSummaryRef = useRef<string | null>(null);
    const resolveRequestIdRef = useRef(0);
    const [createReportFile, setCreateReportFile] = useState<File | null>(null);
    const reportInputRef = useRef<HTMLInputElement>(null);
    const reportTargetRef = useRef<Todo | null>(null);

    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkSubmitting, setLinkSubmitting] = useState(false);
    const [linkTarget, setLinkTarget] = useState<Todo | null>(null);
    const [linkForm] = Form.useForm<{ trackerLink: string }>();
    const [contextMenu, setContextMenu] = useState<EpicContextMenuState | null>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    const closeContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    useEffect(() => {
        if (!contextMenu) {
            return undefined;
        }

        const onPointerDown = (event: PointerEvent | MouseEvent) => {
            const target = event.target as Node | null;
            if (contextMenuRef.current && target && contextMenuRef.current.contains(target)) {
                return;
            }
            closeContextMenu();
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeContextMenu();
            }
        };

        const onScroll = () => {
            closeContextMenu();
        };

        // pointerdown: клик по пустой доске (пан) делает preventDefault и глушит mousedown
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('mousedown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('resize', closeContextMenu);
        window.addEventListener('scroll', onScroll, true);

        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('mousedown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('resize', closeContextMenu);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [contextMenu, closeContextMenu]);

    const openEpicContextMenu = (event: ReactMouseEvent, todo: Todo) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            todo,
            x: event.clientX,
            y: event.clientY,
        });
    };

    const [updateTodo] = useUpdateTodoMutation();
    const [createTodo] = useCreateTodoMutation();
    const [archiveTodo] = useArchiveTodoMutation();
    const [restoreTodo] = useRestoreTodoMutation();
    const [resolveTrackerIssue, { isLoading: resolvingTracker }] = useResolveTrackerIssueMutation();
    const [uploadTodoReport] = useUploadTodoReportMutation();
    const [deleteTodoReport] = useDeleteTodoReportMutation();

    const { data, error, isLoading } = useGetTodosQuery({ projectUrl });
    const {
        data: archivedData,
        error: archivedError,
        isLoading: archivedLoading,
        isFetching: archivedFetching,
    } = useGetArchivedTodosQuery({ projectUrl }, { skip: !showArchive });

    const openCreateModal = () => {
        createForm.resetFields();
        autoFilledSummaryRef.current = null;
        resolveRequestIdRef.current += 1;
        setCreateReportFile(null);
        setCreateModalOpen(true);
    };

    useEffect(() => {
        if (!createModalOpen) {
            return undefined;
        }

        const raw = (createTrackerLink || '').trim();
        if (!raw || !TRACKER_KEY_RE.test(raw)) {
            return undefined;
        }

        const requestId = ++resolveRequestIdRef.current;
        const timer = window.setTimeout(async () => {
            try {
                const result = await resolveTrackerIssue({ trackerLink: raw }).unwrap();
                if (requestId !== resolveRequestIdRef.current) {
                    return;
                }

                const summary = (result.message?.summary || '').trim();
                if (!summary) {
                    return;
                }

                const currentName = (createForm.getFieldValue('todoName') || '').trim();
                const canAutofill = !currentName
                    || currentName === autoFilledSummaryRef.current;

                if (canAutofill) {
                    createForm.setFieldsValue({ todoName: summary });
                    autoFilledSummaryRef.current = summary;
                }
            } catch {
                // тихий fail при наборе — ошибки покажем при создании эпика
            }
        }, 450);

        return () => {
            window.clearTimeout(timer);
        };
    }, [createTrackerLink, createModalOpen, createForm, resolveTrackerIssue]);

    const handleCreateTodoSubmit = async () => {
        try {
            const values = await createForm.validateFields();
            setCreateSubmitting(true);
            const trackerLink = values.trackerLink?.trim();
            const created = await createTodo({
                todoName: values.todoName.trim(),
                projectUrl,
                ...(trackerLink ? { trackerLink } : {}),
            }).unwrap();
            const todoId = Number(created?.message?.todoId);
            const reportFile = createReportFile;
            setCreateModalOpen(false);
            createForm.resetFields();
            autoFilledSummaryRef.current = null;
            setCreateReportFile(null);
            if (todoId && reportFile) {
                try {
                    await uploadTodoReport({
                        todoId,
                        projectUrl,
                        file: reportFile,
                    }).unwrap();
                } catch {
                    // эпик уже создан; ошибка загрузки отчёта показывается из store
                }
            }
        } catch {
            // validation or API error (notification from store)
        } finally {
            setCreateSubmitting(false);
        }
    };

    const openLinkModal = (todo: Todo) => {
        setLinkTarget(todo);
        linkForm.setFieldsValue({
            trackerLink: todo.trackerKey || todo.todoLink || '',
        });
        setLinkModalOpen(true);
    };

    const handleLinkTodoSubmit = async () => {
        if (!linkTarget) {
            return;
        }
        try {
            const values = await linkForm.validateFields();
            setLinkSubmitting(true);
            await updateTodo({
                todoId: linkTarget.todoId,
                projectUrl,
                trackerLink: values.trackerLink.trim(),
            }).unwrap();
            setLinkModalOpen(false);
            setLinkTarget(null);
            linkForm.resetFields();
        } catch {
            // validation or API error
        } finally {
            setLinkSubmitting(false);
        }
    };

    const handleUnlinkTodo = async (todo: Todo) => {
        const ok = await confirm({
            title: 'Отвязать Трекер',
            message: todo.trackerKey
                ? `Отвязать задачу ${todo.trackerKey} от эпика «${todo.todoName}»?`
                : `Отвязать задачу Трекера от эпика «${todo.todoName}»?`,
            okText: 'Отвязать',
        });
        if (!ok) {
            return;
        }
        await updateTodo({
            todoId: todo.todoId,
            projectUrl,
            trackerLink: null,
        });
    };

    const openHtmlReport = (todo: Todo) => {
        const url = resolvePublicUrl(todo.reportPath);
        if (!url) {
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const openReportPicker = (todo: Todo) => {
        reportTargetRef.current = todo;
        if (reportInputRef.current) {
            reportInputRef.current.value = '';
            reportInputRef.current.click();
        }
    };

    const handleReportFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        const todo = reportTargetRef.current;
        reportTargetRef.current = null;
        if (!file || !todo) {
            return;
        }

        const error = validateHtmlReportFile(file);
        if (error) {
            getNotificationInstance()?.error({
                message: 'Ошибка',
                description: error,
            });
            return;
        }

        await uploadTodoReport({
            todoId: todo.todoId,
            projectUrl,
            file,
        });
    };

    const handleDeleteHtmlReport = async (todo: Todo) => {
        if (!todo.reportPath) {
            return;
        }
        const ok = await confirm({
            title: 'Удалить HTML отчёт',
            message: `Удалить HTML-отчёт у эпика «${todo.todoName}»?`,
            okText: 'Удалить',
        });
        if (!ok) {
            return;
        }
        await deleteTodoReport({
            todoId: todo.todoId,
            projectUrl,
        });
    };

    useEffect(() => {
        const savedTodos = localStorage.getItem('selectTodos');
        if (savedTodos) {
            setSelectedTodos(JSON.parse(savedTodos));
        }
    }, []);

    useEventListener('keydown', (event) => {
        const activeElement = document.activeElement;
        const isInputElement = activeElement && (
            activeElement.tagName === 'INPUT'
                || activeElement.tagName === 'TEXTAREA'
                || activeElement.getAttribute('contenteditable'));

        if (isInputElement) {
            return;
        }

        if ((event.key === 'e' || event.key === 'b') && event.ctrlKey) {
            event.preventDefault();
            event.stopPropagation();
            setCollapsed(!collapsed);
        }
    });

    const handleRenameTodo = async (todoId: number, todoName: string) => {
        const newTodoName = await prompt({
            title: 'Переименование эпика',
            value: todoName,
        });
        if (newTodoName) {
            await updateTodo({
                projectUrl,
                todoName: newTodoName,
                todoId,
            });
        }
    };

    const handleArchiveTodo = async (todoId: number, todoName: string) => {
        const ok = await confirm({
            title: 'В архив',
            message: `Отправить эпик «${todoName}» в архив?`,
            okText: 'В архив',
        });
        if (!ok) {
            return;
        }

        await archiveTodo({ todoId, projectUrl, todoName });
        if (activeTodoId === todoId) {
            navigate(`/projects/${projectUrl}`);
        }
    };

    const handleRestoreTodo = async (todo: Todo) => {
        await restoreTodo({
            todoId: todo.todoId,
            projectUrl,
            todoName: todo.todoName,
            trackerKey: todo.trackerKey,
        });
    };

    const toggleFavorite = (todoId: number) => {
        const savedTodos = localStorage.getItem('selectTodos');
        let favoriteTodos: number[] = savedTodos ? JSON.parse(savedTodos) : [];

        if (favoriteTodos.includes(todoId)) {
            favoriteTodos = favoriteTodos.filter(id => id !== todoId);
        } else {
            favoriteTodos.push(todoId);
        }

        localStorage.setItem('selectTodos', JSON.stringify(favoriteTodos));
        setSelectedTodos(favoriteTodos);
    };

    const sourceTodos = useMemo(() => {
        if (showArchive) {
            return archivedData?.message.todos ?? [];
        }
        return data?.message.todos ?? [];
    }, [showArchive, archivedData, data]);

    const sortedTodos = useMemo(() => {
        return [...sourceTodos].sort((a, b) => {
            if (showArchive) {
                return b.todoId - a.todoId;
            }
            const aFav = selectedTodos.includes(a.todoId) ? 0 : 1;
            const bFav = selectedTodos.includes(b.todoId) ? 0 : 1;
            if (aFav !== bFav) return aFav - bFav;
            return b.todoId - a.todoId;
        });
    }, [sourceTodos, selectedTodos, showArchive]);

    const filteredTodos = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return sortedTodos;
        }

        return sortedTodos.filter((todo) => {
            const byName = todo.todoName.toLowerCase().includes(query);
            const byId = String(todo.todoId).includes(query);
            const byKey = todo.trackerKey?.toLowerCase().includes(query);
            return byName || byId || byKey;
        });
    }, [sortedTodos, search]);

    const openSearch = () => {
        setCollapsed(false);
        setTimeout(() => searchInputRef.current?.focus?.(), 100);
    };

    const siderStyle = {
        background: token.colorBgContainer,
        borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
        color: token.colorText,
    };

    const listLoading = showArchive ? (archivedLoading || archivedFetching) : isLoading;
    const listError = showArchive ? archivedError : error;
    const listReady = showArchive ? Boolean(archivedData) : Boolean(data);

    if (!showArchive && isLoading) {
        return (
            <Sider width={280} collapsedWidth={112} collapsible collapsed={collapsed} onCollapse={setCollapsed} className="app-sider" style={siderStyle}>
                <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
            </Sider>
        );
    }

    if (!showArchive && (error || !data)) {
        return (
            <Sider width={280} collapsedWidth={112} collapsible collapsed={collapsed} onCollapse={setCollapsed} className="app-sider" style={siderStyle}>
                <div className="app-empty" style={{ margin: 12 }}>Не удалось загрузить эпики</div>
            </Sider>
        );
    }

    return (
        <Sider
            width={280}
            collapsedWidth={112}
            collapsible
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
            className="app-sider"
            style={siderStyle}
        >
            <div className="app-sider__actions">
                {!showArchive && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        block={!collapsed}
                    >
                        {collapsed ? null : 'Эпик'}
                    </Button>
                )}

                <Tooltip title={showArchive ? 'К активным эпикам' : 'Архив эпиков'} placement="right">
                    <Button
                        type={showArchive ? 'primary' : 'default'}
                        icon={<InboxOutlined />}
                        onClick={() => {
                            setShowArchive((v) => !v);
                            setSearch('');
                        }}
                        block={!collapsed}
                        style={{ marginTop: showArchive ? 0 : 8 }}
                    >
                        {collapsed ? null : (showArchive ? 'К эпикам' : 'Архив')}
                    </Button>
                </Tooltip>

                {collapsed ? (
                    <Tooltip title="Поиск эпиков" placement="right">
                        <Button
                            type="text"
                            icon={<SearchOutlined />}
                            onClick={openSearch}
                            style={{ color: token.colorText, width: '100%', marginTop: 8 }}
                        />
                    </Tooltip>
                ) : (
                    <Input
                        ref={searchInputRef}
                        allowClear
                        prefix={<SearchOutlined style={{ color: token.colorTextSecondary }} />}
                        placeholder="Название или №…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ marginTop: 8 }}
                    />
                )}
            </div>

            {showArchive && !collapsed && (
                <div className="app-sider-archive-hint" style={{ color: token.colorTextSecondary }}>
                    Эпики без изменений больше месяца архивируются автоматически
                </div>
            )}

            {listLoading && showArchive ? (
                <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
            ) : listError || !listReady ? (
                <div className="app-empty" style={{ margin: 12 }}>Не удалось загрузить архив</div>
            ) : filteredTodos.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={collapsed ? null : (
                        search.trim()
                            ? 'Ничего не найдено'
                            : (showArchive ? 'Архив пуст' : 'Пока нет эпиков')
                    )}
                    style={{ marginTop: 24 }}
                />
            ) : (
                <nav className="app-sider-nav">
                    {filteredTodos.map((todo) => {
                        const isFavorite = selectedTodos.includes(todo.todoId);
                        const isActive = activeTodoId === todo.todoId;

                        return (
                            <Tooltip
                                key={todo.todoId}
                                title={collapsed
                                    ? `${todo.todoId} · ${todo.todoName}${todo.trackerKey ? ` · ${todo.trackerKey}` : ''}`
                                    : undefined}
                                placement="right"
                            >
                                <Link
                                    to={`/projects/${projectUrl}/${todo.todoId}`}
                                    onContextMenu={(event) => openEpicContextMenu(event, todo)}
                                    className={[
                                        'app-sider-item',
                                        collapsed ? 'app-sider-item--collapsed' : '',
                                        isActive ? 'app-sider-item--active' : '',
                                        isFavorite && !showArchive ? 'app-sider-item--favorite' : '',
                                        showArchive ? 'app-sider-item--archived' : '',
                                    ].filter(Boolean).join(' ')}
                                    style={{
                                        color: isActive
                                            ? token.colorPrimary
                                            : isFavorite && !showArchive
                                                ? 'var(--ant-gold, #faad14)'
                                                : token.colorText,
                                    }}
                                >
                                    {isFavorite && !showArchive && <StarFilled className="app-sider-item__star" />}
                                    <span className="app-sider-item__id">
                                        {collapsed && todo.trackerKey ? todo.trackerKey : todo.todoId}
                                    </span>
                                    {!collapsed && (
                                        <span className="app-sider-item__name">
                                            <TruncatedEpicName text={todo.todoName} />
                                            {todo.trackerKey ? (
                                                <span className="app-sider-item__tracker-key">
                                                    {todo.trackerKey}
                                                </span>
                                            ) : null}
                                        </span>
                                    )}
                                    {!collapsed && showArchive && (
                                        <RollbackOutlined
                                            className="app-sider-item__restore"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleRestoreTodo(todo);
                                            }}
                                            title="Восстановить"
                                        />
                                    )}
                                </Link>
                            </Tooltip>
                        );
                    })}
                </nav>
            )}

            {contextMenu && createPortal(
                <div
                    ref={contextMenuRef}
                    className="app-sider-context-menu"
                    style={{
                        position: 'fixed',
                        left: contextMenu.x,
                        top: contextMenu.y,
                        zIndex: 1100,
                        width: 280,
                        background: token.colorBgElevated,
                        color: token.colorText,
                        boxShadow: token.boxShadowSecondary,
                        border: `1px solid ${token.colorBorderSecondary}`,
                    }}
                >
                    <Menu
                        mode="vertical"
                        selectable={false}
                        inlineCollapsed={false}
                        style={{
                            width: 280,
                            borderInlineEnd: 'none',
                            background: token.colorBgElevated,
                            color: token.colorText,
                        }}
                        items={(() => {
                            const todo = contextMenu.todo;
                            const isFavorite = selectedTodos.includes(todo.todoId);
                            const hasTracker = Boolean(todo.trackerKey);
                            const hasReport = Boolean(todo.reportPath);
                            const run = (action: () => void) => {
                                closeContextMenu();
                                action();
                            };

                            const reportItems: MenuProps['items'] = [
                                ...(hasReport
                                    ? [{
                                        key: 'open-html-report',
                                        label: 'HTML отчет',
                                        icon: <FileTextOutlined />,
                                        onClick: () => run(() => openHtmlReport(todo)),
                                    }]
                                    : []),
                                {
                                    key: 'attach-html-report',
                                    label: hasReport ? 'Заменить HTML отчёт' : 'Прикрепить HTML отчёт',
                                    icon: <UploadOutlined />,
                                    onClick: () => run(() => openReportPicker(todo)),
                                },
                                ...(hasReport
                                    ? [{
                                        key: 'delete-html-report',
                                        label: 'Удалить HTML отчёт',
                                        icon: <DeleteOutlined />,
                                        danger: true,
                                        onClick: () => run(() => handleDeleteHtmlReport(todo)),
                                    }]
                                    : []),
                            ];

                            const items: MenuProps['items'] = showArchive
                                ? [
                                    {
                                        key: 'open',
                                        label: 'Открыть в новой вкладке',
                                        onClick: () => run(() => window.open(
                                            `/projects/${projectUrl}/${todo.todoId}`,
                                            '_blank',
                                        )),
                                    },
                                    ...reportItems,
                                    {
                                        key: 'restore',
                                        label: 'Восстановить из архива',
                                        icon: <RollbackOutlined />,
                                        onClick: () => run(() => handleRestoreTodo(todo)),
                                    },
                                ]
                                : [
                                    {
                                        key: 'open',
                                        label: 'Открыть в новой вкладке',
                                        onClick: () => run(() => window.open(
                                            `/projects/${projectUrl}/${todo.todoId}`,
                                            '_blank',
                                        )),
                                    },
                                    {
                                        key: 'rename',
                                        label: 'Переименовать',
                                        onClick: () => run(() => handleRenameTodo(todo.todoId, todo.todoName)),
                                    },
                                    {
                                        key: 'favorite',
                                        label: isFavorite ? 'Убрать из избранного' : 'Добавить в избранное',
                                        onClick: () => run(() => toggleFavorite(todo.todoId)),
                                    },
                                    ...reportItems,
                                    {
                                        key: 'bind-tracker',
                                        label: hasTracker ? 'Изменить привязку к Трекеру' : 'Привязать к Трекеру',
                                        icon: <LinkOutlined />,
                                        onClick: () => run(() => openLinkModal(todo)),
                                    },
                                    ...(hasTracker
                                        ? [
                                            {
                                                key: 'open-tracker',
                                                label: 'Открыть Yandex Tracker',
                                                onClick: () => run(() => window.open(
                                                    `https://tracker.yandex.ru/${todo.trackerKey}`,
                                                    '_blank',
                                                )),
                                            },
                                            {
                                                key: 'unlink-tracker',
                                                label: 'Отвязать от Трекера',
                                                onClick: () => run(() => handleUnlinkTodo(todo)),
                                            },
                                        ]
                                        : []),
                                    {
                                        key: 'archive',
                                        label: 'В архив',
                                        icon: <InboxOutlined />,
                                        danger: true,
                                        onClick: () => run(() => handleArchiveTodo(todo.todoId, todo.todoName)),
                                    },
                                ];

                            return items;
                        })()}
                    />
                </div>,
                document.body,
            )}

            <Modal
                title="Добавление эпика"
                open={createModalOpen}
                onOk={handleCreateTodoSubmit}
                onCancel={() => {
                    setCreateModalOpen(false);
                    createForm.resetFields();
                    autoFilledSummaryRef.current = null;
                    setCreateReportFile(null);
                }}
                okText="Создать"
                cancelText="Отмена"
                confirmLoading={createSubmitting}
                destroyOnHidden
            >
                <Form form={createForm} layout="vertical" requiredMark="optional">
                    <Form.Item
                        name="trackerLink"
                        label="Задача в Яндекс Трекере"
                        extra="Если указать ключ или ссылку — название подставится само"
                    >
                        <Input
                            placeholder="GBR-123 или https://tracker.yandex.ru/GBR-123"
                            autoFocus
                            allowClear
                            suffix={resolvingTracker ? <Spin size="small" /> : <span />}
                        />
                    </Form.Item>
                    <Form.Item
                        name="todoName"
                        label="Название"
                        required
                        rules={[{ required: true, whitespace: true, message: 'Введите название эпика' }]}
                        extra="Можно ввести вручную, если эпик без привязки к Трекеру"
                    >
                        <Input
                            placeholder="Название эпика"
                            onChange={(e) => {
                                const value = e.target.value.trim();
                                if (value !== autoFilledSummaryRef.current) {
                                    autoFilledSummaryRef.current = null;
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label="HTML-отчёт для тестировщиков"
                        extra="Необязательно. Можно прикрепить позже через меню эпика"
                    >
                        <Upload
                            accept={HTML_REPORT_ACCEPT}
                            maxCount={1}
                            fileList={createReportFile
                                ? [{ uid: 'report', name: createReportFile.name, status: 'done' }]
                                : []}
                            beforeUpload={(file) => {
                                const error = validateHtmlReportFile(file);
                                if (error) {
                                    getNotificationInstance()?.error({
                                        message: 'Ошибка',
                                        description: error,
                                    });
                                    return Upload.LIST_IGNORE;
                                }
                                setCreateReportFile(file);
                                return false;
                            }}
                            onRemove={() => {
                                setCreateReportFile(null);
                            }}
                        >
                            <Button icon={<UploadOutlined />}>Выбрать HTML</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={linkTarget?.trackerKey ? 'Изменить привязку к Трекеру' : 'Привязать к Трекеру'}
                open={linkModalOpen}
                onOk={handleLinkTodoSubmit}
                onCancel={() => {
                    setLinkModalOpen(false);
                    setLinkTarget(null);
                    linkForm.resetFields();
                }}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={linkSubmitting}
                destroyOnHidden
            >
                <Form form={linkForm} layout="vertical" requiredMark={false}>
                    <Form.Item
                        name="trackerLink"
                        label="Задача в Яндекс Трекере"
                        rules={[{ required: true, message: 'Укажите ключ или ссылку' }]}
                        extra="Ключ или ссылка, например GBR-123"
                    >
                        <Input placeholder="GBR-123 или https://tracker.yandex.ru/GBR-123" autoFocus />
                    </Form.Item>
                </Form>
            </Modal>

            {promptModal}
            {confirmModal}
            <input
                ref={reportInputRef}
                type="file"
                accept={HTML_REPORT_ACCEPT}
                style={{ display: 'none' }}
                onChange={handleReportFileSelected}
            />
        </Sider>
    );
};

export default TodosSider;
